import type { ResumeSectionType } from '@applicator/shared'

import { getSelectedTargetTitle, mergeTargetTitleOptions } from '../../utils/targetTitleUtils'
import type { OptimizationQualityEval } from './qualityEval'

export interface QualityIssue {
  id: string
  sectionTypes: ResumeSectionType[]
  sectionLabel: string
  recommendation: string
  /** Step-9 verdict headline when recommendation is a change detail */
  reviewHeadline?: string
}

export interface SummarySelfHealMeta {
  applied?: boolean
  rounds?: number
  attempted_gate_ids?: string[]
  remaining_gate_ids?: string[]
  manual_only_gate_ids?: string[]
  clarifying_questions?: string[]
  unsupported_gate_themes?: Record<string, string[]>
}

export interface SectionReviewCollectOptions {
  /** Hide target_title step-9 review when title already matches extracted JD title */
  suppressTargetTitleReview?: boolean
  /** Hide skills step-9 prose when structured skill-gap panel is shown */
  suppressSkillsSectionReview?: boolean
  /** Hide work_experience step-9 prose after automated WX self-heal */
  suppressWorkExperienceSectionReview?: boolean
}

export function normalizeTitleForCompare(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:!?]+$/g, '')
}

/** Active included target title lines from resume content. */
export function activeTargetTitlesFromContent(
  targetTitleContent: unknown
): string[] {
  if (!Array.isArray(targetTitleContent)) return []
  // Teal parity: string[] with index 0 = selected title (see targetTitleUtils.ts).
  const allStrings = targetTitleContent.every(
    (row) => row == null || typeof row === 'string'
  )
  if (allStrings) {
    return mergeTargetTitleOptions(targetTitleContent).filter(Boolean)
  }
  return targetTitleContent
    .filter((row) => row && typeof row === 'object' && (row as { included?: boolean }).included !== false)
    .map((row) => String((row as { text?: string }).text ?? '').trim())
    .filter(Boolean)
}

export function targetTitleAlignedWithExtracted(
  targetTitleContent: unknown,
  extractedTitle: string | undefined
): boolean {
  const expected = String(extractedTitle ?? '').trim()
  if (!expected) return false
  const normExpected = normalizeTitleForCompare(expected)
  return activeTargetTitlesFromContent(targetTitleContent).some(
    (t) => normalizeTitleForCompare(t) === normExpected
  )
}

export function extractedTargetTitleFromRun(
  feedbackJson: Record<string, unknown> | null | undefined,
  blocks: Array<{ section_type?: string; agent_feedback?: Record<string, unknown> }> | undefined
): string | undefined {
  const apply = feedbackJson?.apply as { target_title?: { text?: string } } | undefined
  const fromApply = String(apply?.target_title?.text ?? '').trim()
  if (fromApply) return fromApply
  const meta = feedbackJson?.meta as { job_title?: string } | undefined
  if (meta?.job_title?.trim()) return meta.job_title.trim()
  const ttBlock = blocks?.find((b) => b.section_type === 'target_title')
  const fromBlock = String(ttBlock?.agent_feedback?.extracted_title ?? '').trim()
  return fromBlock || undefined
}

function shouldShowPsGateAfterSelfHeal(gateId: string, meta: SummarySelfHealMeta | undefined): boolean {
  if (!meta?.applied) return true
  const upper = gateId.toUpperCase()
  if (!upper.startsWith('PS')) return true
  if (meta.manual_only_gate_ids?.includes(upper)) return true
  if (meta.remaining_gate_ids?.includes(upper)) return true
  if (meta.attempted_gate_ids?.includes(upper)) return false
  return true
}

function professionalSummaryReviewNeedsChanges(
  feedbackJson: Record<string, unknown> | null | undefined
): boolean {
  const reviews = feedbackJson?.section_reviews
  if (!Array.isArray(reviews)) return false
  for (const raw of reviews) {
    if (!raw || typeof raw !== 'object') continue
    const review = raw as Record<string, unknown>
    if (String(review.section_id ?? '') !== 'professional_summary') continue
    return String(review.status ?? '').trim() === 'needs_changes'
  }
  return false
}

export function filterIssuesBySummarySelfHeal(
  issues: QualityIssue[],
  feedbackJson: Record<string, unknown> | null | undefined
): QualityIssue[] {
  const meta = feedbackJson?.summary_self_heal as SummarySelfHealMeta | undefined
  if (!meta?.applied) return issues
  return issues.filter((issue) => {
    const match = issue.id.match(/^gate:(PS\d+):/)
    if (!match) return true
    return shouldShowPsGateAfterSelfHeal(match[1], meta)
  })
}

function professionalSummaryAutomationRan(
  feedbackJson: Record<string, unknown> | null | undefined
): boolean {
  const meta = feedbackJson?.summary_self_heal as SummarySelfHealMeta | undefined
  if (meta?.applied || (meta?.rounds ?? 0) > 0) return true
  const apply = feedbackJson?.apply as
    | { professional_summary?: { action?: string; text?: string } }
    | undefined
  const ps = apply?.professional_summary
  return ps?.action === 'replace' && Boolean(String(ps?.text ?? '').trim())
}

/** Hide stale PS feedback after step-9 apply / self-heal (gates, programmatic, section_reviews). */
export function filterProfessionalSummaryIssuesAfterAutomation(
  issues: QualityIssue[],
  feedbackJson: Record<string, unknown> | null | undefined
): QualityIssue[] {
  let filtered = filterIssuesBySummarySelfHeal(issues, feedbackJson)
  if (!professionalSummaryAutomationRan(feedbackJson)) return filtered

  const meta = feedbackJson?.summary_self_heal as SummarySelfHealMeta | undefined
  const attempted = new Set(
    (meta?.attempted_gate_ids ?? []).map((id) => id.toUpperCase())
  )

  return filtered.filter((issue) => {
    if (!issue.sectionTypes.includes('professional_summary')) return true

    const psStillNeedsChanges = professionalSummaryReviewNeedsChanges(feedbackJson)

    if (issue.id.startsWith('s9:professional_summary:')) {
      return psStillNeedsChanges
    }

    if (issue.id.startsWith('sum:')) {
      const code = issue.id.slice('sum:'.length)
      if (psStillNeedsChanges) return true
      if ((meta?.rounds ?? 0) > 0) return false
      if (code === 'summary_too_long' && attempted.has('PS3')) return false
      if (code === 'summary_em_dash' && attempted.has('PS3')) return false
      if (code.startsWith('summary_banned_phrase') && attempted.has('PS3')) return false
    }

    return true
  })
}

export interface QualityIssueSectionGroup {
  sectionLabel: string
  sectionTypes: ResumeSectionType[]
  recommendations: string[]
}

export const SECTION_DISPLAY_LABELS: Record<ResumeSectionType, string> = {
  contact_info: 'Contact',
  target_title: 'Target title',
  professional_summary: 'Professional summary',
  work_experience: 'Work experience',
  education: 'Education',
  skills: 'Skills',
  interests: 'Interests',
  certifications: 'Certifications',
  awards_scholarships: 'Awards & scholarships',
  projects: 'Projects',
  volunteering_leadership: 'Volunteering & leadership',
  publications: 'Publications',
}

const SECTION_ORDER: ResumeSectionType[] = [
  'contact_info',
  'target_title',
  'professional_summary',
  'work_experience',
  'education',
  'skills',
  'projects',
  'certifications',
  'interests',
  'awards_scholarships',
  'volunteering_leadership',
  'publications',
]

const GATE_PREFIX_SECTION: Array<{ prefix: string; section: ResumeSectionType }> = [
  { prefix: 'T', section: 'target_title' },
  { prefix: 'PS', section: 'professional_summary' },
  { prefix: 'SK', section: 'skills' },
  { prefix: 'ED', section: 'education' },
  { prefix: 'CH', section: 'contact_info' },
  { prefix: 'WE', section: 'work_experience' },
  { prefix: 'PR', section: 'projects' },
  { prefix: 'WB', section: 'work_experience' },
]

const PROGRAMMATIC_EDUCATION: Record<string, string> = {
  education_missing:
    'Add at least one education entry and include it on the resume.',
  education_none_included:
    'Turn on at least one education entry so it appears on the resume.',
  education_missing_institution:
    'Add the school or university name for each education entry included on the resume.',
  education_end_before_start:
    'Set the education end date to the same month as the start date or later.',
}

const PROGRAMMATIC_SUMMARY: Record<string, string> = {
  summary_empty:
    'Write a professional summary that highlights your fit for this role.',
  summary_too_long: 'Shorten your professional summary to under 600 characters.',
  summary_em_dash:
    'Replace em dashes in your summary with commas or separate sentences.',
  summary_duplicate_including:
    'Remove the duplicated word "including" in your professional summary.',
  summary_duplicate_and:
    'Remove the duplicated word "and" in your professional summary.',
  summary_semicolon:
    'Rewrite semicolon chains in your professional summary as full sentences.',
  summary_telegraphic_fragments:
    'Merge telegraphic period fragments into connected sentences.',
  summary_false_sentence_break:
    'Fix broken sentence joins in your professional summary.',
  summary_lowercase_after_period:
    'Capitalize the first word after each sentence period in your summary.',
  summary_false_and_join:
    'Fix mid-sentence "And" joins in your professional summary.',
  summary_mid_sentence_caps:
    'Lowercase common terms mid-sentence in your professional summary.',
}

const META_GATE_PREFIX = 'META-'
const INTERNAL_GATE_PATTERN = /^[A-Z]{2,3}\d+$/i
const INTERNAL_REASON_KEY_PATTERN =
  /\b(education_missing|education_none_included|education_missing_institution|education_end_before_start|summary_empty|summary_too_long|summary_em_dash|summary_banned_phrase|mindera_[a-z0-9_]+|format check)\b/i

function sectionTypesForGateId(gateId: string): ResumeSectionType[] {
  const upper = gateId.toUpperCase()
  for (const { prefix, section } of GATE_PREFIX_SECTION) {
    if (upper.startsWith(prefix)) return [section]
  }
  if (upper.startsWith('META')) return []
  return []
}

function primarySectionLabel(sectionTypes: ResumeSectionType[]): string {
  if (!sectionTypes.length) return 'Resume'
  const ordered = SECTION_ORDER.filter((st) => sectionTypes.includes(st))
  return SECTION_DISPLAY_LABELS[ordered[0] ?? sectionTypes[0]]
}

function recommendationForEducationProgrammatic(code: string): string {
  if (PROGRAMMATIC_EDUCATION[code]) return PROGRAMMATIC_EDUCATION[code]
  if (code.startsWith('summary_banned_phrase')) {
    return 'Revise wording in your professional summary to sound more natural.'
  }
  return 'Review your education section and fix missing or incomplete entries.'
}

function recommendationForSummaryProgrammatic(code: string): string {
  if (PROGRAMMATIC_SUMMARY[code]) return PROGRAMMATIC_SUMMARY[code]
  if (code.startsWith('summary_banned_phrase')) {
    return 'Remove AI-sounding or overused phrases from your professional summary.'
  }
  return 'Review your professional summary formatting and tone.'
}

function recommendationForPs3(reason: string, evidence?: Record<string, unknown>): string {
  const lower = reason.toLowerCase()
  const violations = evidence?.violations
  if (Array.isArray(violations)) {
    for (const raw of violations) {
      const code = String(raw ?? '').trim()
      if (PROGRAMMATIC_SUMMARY[code]) return PROGRAMMATIC_SUMMARY[code]
      if (code.startsWith('summary_banned_phrase')) {
        return 'Remove AI-sounding or overused phrases from your professional summary.'
      }
    }
  }
  for (const [code, message] of Object.entries(PROGRAMMATIC_SUMMARY)) {
    if (lower.includes(code)) return message
  }
  if (lower.includes('summary_banned_phrase')) {
    return 'Remove AI-sounding or overused phrases from your professional summary.'
  }
  return 'Revise your professional summary: remove em dashes, banned phrases, and AI-sounding wording.'
}

function stripInternalSlugs(text: string): string {
  return text
    .replace(/\b[a-z]+_[a-z0-9_]{8,}\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

function recommendationForCh1(reason: string): string {
  const lower = reason.toLowerCase()
  if (lower.includes('substack') || lower.includes('github')) {
    return 'Remove Substack and GitHub from the resume header. Keep LinkedIn, email, phone, and location.'
  }
  const missingMatch = reason.match(/core contact missing[^:]*:\s*(.+)/i)
  if (missingMatch) {
    const fields = missingMatch[1].trim()
    return `Add your ${fields} in Contact so they appear on the resume.`
  }
  if (lower.includes('differs from canonical') || lower.includes('lisbon profile')) {
    return 'Update your contact details to match your standard profile (Lisbon, email, phone, LinkedIn).'
  }
  if (lower.includes('core contact on resume')) {
    return 'Review contact details and keep only core links on the resume.'
  }
  return 'Complete your contact section with name, email, phone, location, and LinkedIn.'
}

function recommendationForWe18(reason: string): string {
  const lower = reason.toLowerCase()
  if (lower.includes('still on')) {
    return 'Turn off work experience roles that should not appear on automated resumes.'
  }
  if (lower.includes('absent from resume')) {
    return 'A role excluded by your resume policy is missing from work experience — review which positions should stay off.'
  }
  if (lower.includes('policy excludes')) {
    return 'Work experience policy is satisfied.'
  }
  return 'Review which work experience roles should be turned off for this resume.'
}

function recommendationForWe19(reason: string): string {
  const lower = reason.toLowerCase()
  if (lower.includes('anchor') && lower.includes('not found')) {
    return 'Your Route4Me Lead Product Manager role is required as a chronology anchor — check work experience order and titles.'
  }
  if (lower.includes('should be off')) {
    return 'Turn off older work experience positions that should not appear on this resume.'
  }
  if (lower.includes('chronology cutoff respected')) {
    return 'Work experience chronology looks correct.'
  }
  return 'Review work experience order and turn off roles older than your chronology cutoff.'
}

function recommendationForWe20(reason: string): string {
  const lower = reason.toLowerCase()
  if (lower.includes('cyrillic')) {
    return 'Turn off or rewrite achievement bullets written in Cyrillic — English resumes should use English bullets only.'
  }
  return 'Review work experience achievement bullets for language and inclusion.'
}

function recommendationForPs5(
  reason: string,
  evidence?: Record<string, unknown>
): string {
  const suggested = Array.isArray(evidence?.suggested_phrases)
    ? (evidence?.suggested_phrases as string[]).filter(Boolean)
    : []
  const jdMentions = Array.isArray(evidence?.jd_mentions)
    ? (evidence?.jd_mentions as string[]).filter(Boolean)
    : []
  const wxEvidence = Array.isArray(evidence?.wx_ai_evidence)
    ? (evidence?.wx_ai_evidence as string[]).filter(Boolean)
    : []

  if (suggested.length) {
    const primary = suggested[0]
    const alt = suggested[1]
    let msg = `In Professional summary, add one sentence that includes «${primary}»`
    if (alt) msg += ` or «${alt}»`
    if (wxEvidence.length) {
      const hint = wxEvidence[0].length > 90 ? `${wxEvidence[0].slice(0, 87)}…` : wxEvidence[0]
      msg += ` — your work experience already mentions this (${hint})`
    } else if (jdMentions.length) {
      const jd = jdMentions[0].length > 80 ? `${jdMentions[0].slice(0, 77)}…` : jdMentions[0]
      msg += ` — the job description asks for: «${jd}»`
    }
    return `${msg}.`
  }

  const match = reason.match(/add one of:\s*([^;]+)/i)
  if (match?.[1]) {
    const phrases = match[1]
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .slice(0, 2)
    if (phrases.length) {
      return `In Professional summary, add one sentence with «${phrases[0]}»${
        phrases[1] ? ` or «${phrases[1]}»` : ''
      }.`
    }
  }

  return 'In Professional summary, add one sentence with a concrete AI theme from the job description (for example LLM or workflow automation).'
}

function recommendationForPs8(
  reason: string,
  evidence?: Record<string, unknown>
): string {
  const userMessage = String(evidence?.user_message ?? '').trim()
  if (userMessage) return userMessage

  const suggested = Array.isArray(evidence?.suggested_phrases)
    ? (evidence?.suggested_phrases as string[]).filter(Boolean)
    : []
  const jdMentions = Array.isArray(evidence?.jd_mentions)
    ? (evidence?.jd_mentions as string[]).filter(Boolean)
    : []
  const wxEvidence = Array.isArray(evidence?.wx_evidence)
    ? (evidence?.wx_evidence as string[]).filter(Boolean)
    : []
  const unsupported = Array.isArray(evidence?.unsupported_themes)
    ? (evidence?.unsupported_themes as Array<Record<string, unknown>>)
    : []
  const supported = Array.isArray(evidence?.supported_themes)
    ? (evidence?.supported_themes as Array<Record<string, unknown>>)
    : []

  if (unsupported.length && !supported.length) {
    const first = unsupported[0]
    const theme = String(first?.theme ?? first?.suggested_phrase ?? '').trim()
    const jdSnippets = Array.isArray(first?.jd_snippets)
      ? (first.jd_snippets as string[]).filter(Boolean)
      : []
    if (theme) {
      const jd = jdSnippets[0]
      if (jd) {
        const short = jd.length > 90 ? `${jd.slice(0, 87)}…` : jd
        return `The job description mentions «${theme}» (for example: «${short}»). It is not on your resume yet. Have you worked with ${theme}?`
      }
      return `The job description mentions «${theme}», but it is not on your resume yet. Have you worked with ${theme}?`
    }
  }

  if (suggested.length) {
    const primary = suggested[0]
    const alt = suggested[1]
    let msg = `In Professional summary, add one sentence that includes «${primary}»`
    if (alt) msg += ` or «${alt}»`
    if (wxEvidence.length) {
      const hint = wxEvidence[0].length > 90 ? `${wxEvidence[0].slice(0, 87)}…` : wxEvidence[0]
      msg += ` — your work experience already mentions this (${hint})`
    } else if (jdMentions.length) {
      const jd = jdMentions[0].length > 80 ? `${jdMentions[0].slice(0, 77)}…` : jdMentions[0]
      msg += ` — the job description asks for: «${jd}»`
    }
    return `${msg}.`
  }

  const missing = Array.isArray(evidence?.missing_sample)
    ? (evidence?.missing_sample as string[]).filter(Boolean).slice(0, 2)
    : []
  if (missing.length) {
    return `In Professional summary, add one sentence that includes «${missing[0]}»${
      missing[1] ? ` or «${missing[1]}»` : ''
    }.`
  }

  const match = reason.match(/missing themes:\s*([^;]+)/i)
  if (match?.[1]) {
    const phrases = match[1]
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .slice(0, 2)
    if (phrases.length) {
      return `In Professional summary, add one sentence that includes «${phrases[0]}»${
        phrases[1] ? ` or «${phrases[1]}»` : ''
      }.`
    }
  }

  return (
    'In Professional summary, add one sentence with a concrete theme from the job ' +
    'description that your work experience or skills already support.'
  )
}

function recommendationForPsGate(
  gateId: string,
  reason: string,
  evidence?: Record<string, unknown>
): string {
  const upper = gateId.toUpperCase()
  const lower = reason.toLowerCase()
  if (upper === 'PS2' || lower.includes('too short') || lower.includes('summary length')) {
    return 'Expand your professional summary to at least a few sentences that sell your fit for the role.'
  }
  if (upper === 'PS3' || lower.includes('writing rules')) {
    return recommendationForPs3(reason, evidence)
  }
  if (upper === 'PS4' || lower.includes('igaming bleed')) {
    return 'Remove gambling or casino terms from your summary unless the role is in that industry.'
  }
  if (upper === 'PS5' || lower.includes('missing ai focus') || lower.includes('missing ai signals')) {
    return recommendationForPs5(reason, evidence)
  }
  if (upper === 'PS6' || lower.includes('missing igaming focus')) {
    return 'Add iGaming or compliance themes to your summary to match this role.'
  }
  if (upper === 'PS7' || lower.includes('weaker than baseline')) {
    return 'Strengthen your professional summary — the latest version is shorter or weaker than before.'
  }
  if (upper === 'PS8' || lower.includes('missing themes')) {
    return recommendationForPs8(reason, evidence)
  }
  if (upper === 'PS9' || lower.includes('keyword_stuffing') || lower.includes('repeat:')) {
    return 'Break up comma lists and repeated words in your professional summary.'
  }
  if (upper === 'PS10' || lower.includes('too long')) {
    return 'Shorten your professional summary so it stays scannable on one page.'
  }
  if (upper === 'PS13' || lower.includes('no included professional summary')) {
    return 'Add real text to your professional summary and turn it on for the resume.'
  }
  if (upper === 'PS16' || lower.includes('managing/coaching pms')) {
    return 'Add people management and PM coaching to your professional summary — the job requires leading product managers.'
  }
  if (upper === 'PS17' || lower.includes('financial/enterprise data')) {
    return 'Add financial, enterprise, or data-platform themes to your professional summary for this role.'
  }
  if (upper === 'PS18' || lower.includes('solo tinkering')) {
    return 'Frame AI as enabling your PM team (coaching, adoption), not only personal side projects.'
  }
  return 'Review and improve your professional summary for this job.'
}

function recommendationForSkGate(gateId: string, reason: string): string {
  const upper = gateId.toUpperCase()
  const lower = reason.toLowerCase()
  if (upper === 'SK19' || lower.includes('same-category duplicate')) {
    return 'Remove duplicate skills within the same category.'
  }
  if (upper === 'SK20' || lower.includes('cross')) {
    return 'Turn off duplicate skills that appear in more than one category.'
  }
  if (upper === 'SK21' || lower.includes('skills_detail') || lower.includes('block_order')) {
    return 'Align enabled skills with the recommended categories for this role.'
  }
  return 'Review your skills list for duplicates and relevance to the job.'
}

function recommendationForTargetGate(gateId: string, reason: string): string {
  const upper = gateId.toUpperCase()
  const lower = reason.toLowerCase()
  if (upper === 'T14' || lower.includes('dedicated line') || lower.includes('missing dedicated')) {
    return 'Set a target job title and make sure it appears on its own line under your name.'
  }
  if (lower.includes('target title missing')) {
    return 'Add a target job title that matches the role you are applying for.'
  }
  return 'Set or fix your target job title for this application.'
}

function recommendationForEdGate(reason: string): string {
  const lower = reason.toLowerCase()
  if (lower.includes('programmatic education rules passed')) {
    return 'Education section looks complete.'
  }
  if (PROGRAMMATIC_EDUCATION[reason.trim()]) {
    return PROGRAMMATIC_EDUCATION[reason.trim()]
  }
  for (const [code, text] of Object.entries(PROGRAMMATIC_EDUCATION)) {
    if (lower.includes(code.replace(/_/g, ' ')) || lower.includes(code)) {
      return text
    }
  }
  return 'Review your education section and fix missing or incomplete entries.'
}

function recommendationForGate(
  gateId: string,
  rawReason: string,
  evidence?: Record<string, unknown>
): string | null {
  const upper = gateId.toUpperCase()
  if (upper.startsWith(META_GATE_PREFIX) || upper.startsWith('BLOCK-FAIL')) {
    return null
  }
  if (upper.startsWith('REGEN-')) return null
  const reason = stripInternalSlugs(rawReason)
  const lower = reason.toLowerCase()

  if (upper === 'ED-PROG') return recommendationForEdGate(reason)
  if (upper === 'CH1') return recommendationForCh1(reason)
  if (upper === 'WE18') return recommendationForWe18(reason)
  if (upper === 'WE19') return recommendationForWe19(reason)
  if (upper === 'WE20') return recommendationForWe20(reason)
  if (upper === 'WE5' || lower.includes('multi-year gap')) {
    return 'Fix company date ranges so the header span matches real sub-role dates (no implied gap years).'
  }
  if (upper.startsWith('PS')) return recommendationForPsGate(upper, reason, evidence)
  if (upper.startsWith('SK')) return recommendationForSkGate(upper, reason)
  if (upper.startsWith('T')) return recommendationForTargetGate(upper, reason)

  const sections = sectionTypesForGateId(upper)
  const label = primarySectionLabel(sections.length ? sections : ['professional_summary'])
  if (reason && !INTERNAL_GATE_PATTERN.test(reason) && !INTERNAL_REASON_KEY_PATTERN.test(reason)) {
    return `Review ${label.toLowerCase()}: ${reason}`
  }
  return `Review your ${label.toLowerCase()} section and fix issues flagged by automated checks.`
}

/** Test helper: user-facing copy must not leak internal gate or eval identifiers. */
export function userFacingTextLooksClean(text: string): boolean {
  if (!text.trim()) return true
  if (INTERNAL_GATE_PATTERN.test(text.trim())) return false
  if (/\b[A-Z]{2,3}-\d+\b/.test(text)) return false
  if (INTERNAL_REASON_KEY_PATTERN.test(text)) return false
  if (/[а-яА-ЯёЁ]/.test(text)) return false
  if (/\bformat check\b/i.test(text)) return false
  if (/\bquality gate\b/i.test(text)) return false
  if (/\bweave more keywords from the job description\b/i.test(text)) return false
  return true
}

/** Clarifying questions from self-heal (unsupported JD themes not on resume). */
export function collectClarifyingQuestions(
  feedbackJson: Record<string, unknown> | null | undefined
): QualityIssue[] {
  const meta = feedbackJson?.summary_self_heal as SummarySelfHealMeta | undefined
  const questions = meta?.clarifying_questions ?? []
  const issues: QualityIssue[] = []
  const seen = new Set<string>()
  for (const raw of questions) {
    const q = String(raw ?? '').trim()
    if (!q || seen.has(q)) continue
    seen.add(q)
    issues.push({
      id: `clarify:${q.slice(0, 32)}`,
      sectionTypes: ['professional_summary'],
      sectionLabel: primarySectionLabel(['professional_summary']),
      recommendation: q,
    })
  }
  return issues
}

export function groupQualityIssuesBySection(issues: QualityIssue[]): QualityIssueSectionGroup[] {
  const byLabel = new Map<string, QualityIssueSectionGroup>()
  for (const issue of issues) {
    const existing = byLabel.get(issue.sectionLabel)
    if (existing) {
      if (!existing.recommendations.includes(issue.recommendation)) {
        existing.recommendations.push(issue.recommendation)
      }
      for (const st of issue.sectionTypes) {
        if (!existing.sectionTypes.includes(st)) existing.sectionTypes.push(st)
      }
    } else {
      byLabel.set(issue.sectionLabel, {
        sectionLabel: issue.sectionLabel,
        sectionTypes: [...issue.sectionTypes],
        recommendations: [issue.recommendation],
      })
    }
  }
  return SECTION_ORDER.map((st) => SECTION_DISPLAY_LABELS[st])
    .filter((label) => byLabel.has(label))
    .map((label) => byLabel.get(label)!)
    .concat(
      [...byLabel.values()].filter(
        (g) => !SECTION_ORDER.some((st) => SECTION_DISPLAY_LABELS[st] === g.sectionLabel)
      )
    )
}

export function collectQualityIssues(
  qualityEval: OptimizationQualityEval | null | undefined,
  feedbackJson?: Record<string, unknown> | null
): QualityIssue[] {
  if (!qualityEval || qualityEval.primary_pass) return []

  const issues: QualityIssue[] = []
  const seen = new Set<string>()

  const pushIssue = (
    key: string,
    sectionTypes: ResumeSectionType[],
    recommendation: string
  ) => {
    if (seen.has(key)) return
    seen.add(key)
    issues.push({
      id: key,
      sectionTypes,
      sectionLabel: primarySectionLabel(sectionTypes),
      recommendation,
    })
  }

  const prog = qualityEval?.programmatic_failures
  if (prog?.education?.length) {
    for (const code of prog.education) {
      pushIssue(
        `edu:${code}`,
        ['education'],
        recommendationForEducationProgrammatic(code)
      )
    }
  }
  if (prog?.summary?.length) {
    for (const code of prog.summary) {
      pushIssue(
        `sum:${code}`,
        ['professional_summary'],
        recommendationForSummaryProgrammatic(code)
      )
    }
  }

  const gateResults = qualityEval?.gate_results ?? []
  for (const gate of gateResults) {
    const gateId = String(gate.gate_id ?? '')
    if (!gateId) continue
    if (gateId.startsWith('REGEN-')) continue

    const evidence = (gate.evidence ?? undefined) as Record<string, unknown> | undefined
    const clarifyingOnly = evidence?.clarifying_only === true

    if (gate.pass || gate.status === 'skipped') {
      if (!(gateId === 'PS8' && clarifyingOnly)) continue
      if ((evidence as { report_only?: boolean } | undefined)?.report_only) continue
    } else if ((evidence as { report_only?: boolean } | undefined)?.report_only) {
      continue
    }

    const rawReason = String(gate.detail ?? gate.reason ?? gate.message ?? '').trim()
    const gateUserMessage = String(
      (gate as { user_message?: string }).user_message ?? evidence?.user_message ?? ''
    ).trim()
    const recommendation =
      gateUserMessage || recommendationForGate(gateId, rawReason, evidence)
    if (!recommendation) continue

    const sections = sectionTypesForGateId(gateId)
    const sectionTypes = sections.length ? sections : ['professional_summary']

    // Skip ED-PROG when education programmatic failures already surfaced
    if (gateId === 'ED-PROG' && prog?.education?.length) continue
    // Skip PS13 when summary programmatic empty already surfaced
    if (gateId === 'PS13' && prog?.summary?.includes('summary_empty')) continue

    const dedupeKey = `gate:${gateId}:${recommendation.slice(0, 40)}`
    pushIssue(dedupeKey, sectionTypes, recommendation)
  }

  return filterProfessionalSummaryIssuesAfterAutomation(issues, feedbackJson)
}

const SECTION_REVIEW_ID_MAP: Partial<Record<string, ResumeSectionType>> = {
  professional_summary: 'professional_summary',
  work_experience: 'work_experience',
  skills: 'skills',
  contact_info: 'contact_info',
  target_title: 'target_title',
  education: 'education',
  certifications: 'certifications',
  projects: 'projects',
  interests: 'interests',
  awards_scholarships: 'awards_scholarships',
}

/** Step-9 section_reviews → inline quality recommendations (Claude-parity UI). */
export function collectSectionReviewIssues(
  feedbackJson: Record<string, unknown> | null | undefined,
  options: SectionReviewCollectOptions = {}
): QualityIssue[] {
  if (!feedbackJson) return []
  const reviews = feedbackJson.section_reviews
  if (!Array.isArray(reviews)) return []

  const issues: QualityIssue[] = []
  for (const raw of reviews) {
    if (!raw || typeof raw !== 'object') continue
    const review = raw as Record<string, unknown>
    const sectionId = String(review.section_id ?? '').trim()
    const sectionType = SECTION_REVIEW_ID_MAP[sectionId]
    if (!sectionType) continue
    if (sectionId === 'target_title' && options.suppressTargetTitleReview) continue
    if (sectionId === 'skills' && options.suppressSkillsSectionReview) continue
    if (sectionId === 'work_experience' && options.suppressWorkExperienceSectionReview) continue

    const status = String(review.status ?? '').trim()
    if (status !== 'needs_changes') continue

    const sectionTypes: ResumeSectionType[] = [sectionType]
    const verdict = String(review.verdict ?? '').trim()
    const changes = review.changes

    if (verdict) {
      issues.push({
        id: `s9:${sectionId}:verdict`,
        sectionTypes,
        sectionLabel: primarySectionLabel(sectionTypes),
        recommendation: verdict,
      })
    }

    if (Array.isArray(changes) && changes.length) {
      changes.forEach((ch, index) => {
        if (!ch || typeof ch !== 'object') return
        const change = ch as Record<string, unknown>
        const detail = String(change.detail ?? change.action ?? '').trim()
        if (!detail) return
        if (verdict && normalizeTitleForCompare(detail) === normalizeTitleForCompare(verdict)) {
          return
        }
        issues.push({
          id: `s9:${sectionId}:${index}`,
          sectionTypes,
          sectionLabel: primarySectionLabel(sectionTypes),
          recommendation: detail,
          reviewHeadline: verdict || undefined,
        })
      })
    }
  }
  return filterProfessionalSummaryIssuesAfterAutomation(issues, feedbackJson)
}

export function qualityIssueSectionTypes(issues: QualityIssue[]): ResumeSectionType[] {
  const out = new Set<ResumeSectionType>()
  for (const issue of issues) {
    for (const st of issue.sectionTypes) out.add(st)
  }
  return [...out]
}

/** Unique user-facing recommendations for one resume section (inline feedback). */
export function issuesForSection(
  issues: QualityIssue[],
  sectionType: ResumeSectionType
): string[] {
  const recs: string[] = []
  const seen = new Set<string>()
  for (const issue of issues) {
    if (!issue.sectionTypes.includes(sectionType)) continue
    if (seen.has(issue.recommendation)) continue
    seen.add(issue.recommendation)
    recs.push(issue.recommendation)
  }
  return recs
}

/** Map section type → deduped recommendations (single source for panel + inline blocks). */
export function issuesBySection(
  issues: QualityIssue[]
): Partial<Record<ResumeSectionType, string[]>> {
  const out: Partial<Record<ResumeSectionType, string[]>> = {}
  for (const issue of issues) {
    for (const sectionType of issue.sectionTypes) {
      const list = out[sectionType] ?? []
      if (!list.includes(issue.recommendation)) {
        out[sectionType] = [...list, issue.recommendation]
      }
    }
  }
  return out
}
